import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

const sortableInstances = new Set()
let isPageUpdating = false

export default class extends Controller {
  static values = {
    url: String,
    deletePositionConfirmation: String,
    deleteGroupConfirmation: String
  }

  connect() {
    this.initializeSortable()
  }

  disconnect() {
    if (!this.sortable) return

    sortableInstances.delete(this.sortable)
    this.sortable.destroy()
    this.sortable = null
  }

  initializeSortable() {
    this.sortable = Sortable.create(this.element, {
      animation: 150,
      handle: ".drag-and-drop-handle",
      group: "rounds",
      onStart: this.captureDragOrigin.bind(this),
      onEnd: this.updatePosition.bind(this),
      emptyInsertThreshold: 200
    })

    sortableInstances.add(this.sortable)
  }

  async updatePosition(event) {
    if (isPageUpdating) {
      this.revertDrag(event)
      return
    }

    // Dropped back where it was: Sortable still fires, and the request would
    // renumber the round and reload the page for nothing (review #8890).
    if (event.from === event.to && event.oldIndex === event.newIndex) {
      this.clearDragOrigin()
      return
    }

    const item = event.item
    const daySection = event.to.closest('.day-section')

    if (!daySection) {
      this.revertDrag(event)
      return
    }

    const date = daySection.dataset.date

    isPageUpdating = true
    this.setSortablesDisabled(true)

    try {
      // The drop is named by the card it lands BEFORE, not by its index: a card
      // stands for every position of its group, so "index + 1" is not a rank
      // (#8439 QA retest). The server moves the whole card and renumbers.
      await this.patchPosition(item.dataset.id, {
        delivery_date: date,
        before_id: this.nextCardId(item)
      })
      // The stop numbers are drawn by the server: reload to show the new ones.
      window.location.reload()
    } catch (error) {
      console.error(error)
      // A full reload, not `Turbo.visit`: a Turbo visit keeps this module, and
      // `isPageUpdating` with it — every drag after a refused one was then
      // reverted without a request.
      window.location.reload()
    } finally {
      this.clearDragOrigin()
    }
  }

  nextCardId(item) {
    let next = item.nextElementSibling
    while (next && !next.dataset.id) next = next.nextElementSibling

    return next ? next.dataset.id : null
  }

  captureDragOrigin(event) {
    this.dragOriginList = event.from
    this.dragOriginIndex = event.oldIndex
  }

  setSortablesDisabled(disabled) {
    sortableInstances.forEach((sortable) => {
      sortable.option('disabled', disabled)
    })
  }

  revertDrag(event) {
    const { item, from, oldIndex } = event
    const originList = this.dragOriginList || from
    const originIndex = this.dragOriginIndex ?? oldIndex

    if (!item || !originList || originIndex === undefined || originIndex === null) {
      window.Turbo.visit(window.location.href)
      return
    }

    const siblingsWithoutItem = Array.from(originList.children).filter((child) => child !== item)
    const referenceNode = siblingsWithoutItem[originIndex] || null
    originList.insertBefore(item, referenceNode)

    this.clearDragOrigin()
  }

  clearDragOrigin() {
    this.dragOriginList = null
    this.dragOriginIndex = null
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  async patchPosition(positionId, body) {
    const response = await fetch(`/round_positions/${positionId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken()
      },
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      throw new Error(`Failed to update round position ${positionId}`)
    }

    return response
  }

  delete(event) {
    event.preventDefault()
    event.stopPropagation()
    const link = event.currentTarget

    if (confirm(this.deletePositionConfirmationValue)) {
      fetch(link.href, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken(),
          'Accept': 'text/vnd.turbo-stream.html, text/html, application/json'
        }
      }).then(() => window.Turbo.visit(window.location.href)).catch((error) => {
        console.error(error)
        window.Turbo.visit(window.location.href)
      })
    }
  }

  deleteGroup(event) {
    event.preventDefault()
    event.stopPropagation()
    const link = event.currentTarget

    if (confirm(this.deleteGroupConfirmationValue)) {
      fetch(link.href, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken(),
          'Accept': 'text/vnd.turbo-stream.html, text/html, application/json'
        }
      }).then(() => window.Turbo.visit(window.location.href)).catch((error) => {
        console.error(error)
        window.Turbo.visit(window.location.href)
      })
    }
  }
}
